import { useMemo, useRef, useState } from 'react';

/**
 * useStaleRead (row 359 B): what a failed read does to the rows already on screen, decided in ONE place
 * for every Exceptions table.
 *
 * - A failed read of a NEW view (another filter, sort, search or page than the rows on screen belong
 *   to) empties the table: the old rows would stand under the new view's name.
 * - A failed RE-read of the view already on screen (after a write, or Try again) KEEPS its rows. The
 *   table used to empty here too, which collapsed the page and threw the reader to the top. The rows
 *   stay under a red line (`staleAt`, the time it failed) and the screen switches every action off
 *   until a read succeeds, so nobody acts on a stale list about who may be contacted.
 *
 * `ok` and `failed` are stable across renders, so a page may list them in a useCallback's deps.
 */
export interface StaleRead {
  /** '' when the rows on screen are current; otherwise the time the re-read failed, "3:14 AM". */
  staleAt: string;
  /** A read of `viewKey` succeeded. */
  ok: (viewKey: string) => void;
  /** A read of `viewKey` failed. True: keep the rows (they are this view's, now stale). False: clear them. */
  failed: (viewKey: string) => boolean;
}

/** The red line over a stale list. Plain words, one sentence per fact. */
export function staleReadSentence(at: string): string {
  return `Could not refresh the list at ${at}. The rows below are from the last good read, and every action is off until the list reads again.`;
}

export function staleStamp(now: Date = new Date()): string {
  return now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export function useStaleRead(): StaleRead {
  // '' until a read succeeds; a view key is never '' (callers pass a JSON array), so '' matches nothing.
  const loadedKey = useRef('');
  const [staleAt, setStaleAt] = useState('');
  const fns = useMemo(() => ({
    ok(viewKey: string): void {
      loadedKey.current = viewKey;
      setStaleAt('');
    },
    failed(viewKey: string): boolean {
      if (loadedKey.current === viewKey) {
        setStaleAt(staleStamp());
        return true;
      }
      loadedKey.current = '';
      setStaleAt('');
      return false;
    },
  }), []);
  return { staleAt, ...fns };
}
